/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Widget Behavior & Quick Prompts Tab Component (Task 10.3.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { useWidgetStudioStore } from '../../stores/useWidgetStudioStore';
import { useI18n } from '../../hooks/useI18n';
import {
  HelpCircle,
  Plus,
  Trash2,
  Database,
  Layers,
  Sparkles,
  Clock,
} from 'lucide-react';

export const WidgetBehaviorTab: React.FC = () => {
  const { t } = useI18n();
  const {
    config,
    addStarterQuestion,
    removeStarterQuestion,
    updateStarterQuestion,
    setAssociatedKnowledgeBase,
    updateAppearance,
  } = useWidgetStudioStore();

  const [newQuestionText, setNewQuestionText] = useState('');

  const handleAddQuestion = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newQuestionText.trim()) return;
    addStarterQuestion(newQuestionText);
    setNewQuestionText('');
  };

  return (
    <div className="space-y-6">
      {/* 1. Starter Questions (Quick Prompts) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-heading font-bold text-foreground flex items-center gap-2">
            <HelpCircle className="w-4 h-4 text-primary" />
            <span>{t('widget.starterQuestionsHeading')}</span>
          </h3>
          <span className="text-xs text-foreground-muted font-mono">
            {t('widget.questionsCount', { count: config.starterQuestions.length })}
          </span>
        </div>
        <p className="text-xs text-foreground-muted">
          {t('widget.starterQuestionsDesc')}
        </p>

        {/* List of Questions */}
        <div className="space-y-2">
          {config.starterQuestions.map((q, idx) => (
            <div
              key={q.id}
              className="flex items-center gap-2 p-2.5 rounded-xl border border-surface-border bg-surface-elevated/60 transition-colors focus-within:border-primary/50"
            >
              <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                {idx + 1}
              </span>
              <input
                type="text"
                value={q.question}
                onChange={(e) => updateStarterQuestion(q.id, e.target.value)}
                placeholder={t('widget.starterPlaceholder')}
                className="flex-1 bg-transparent text-xs text-foreground focus:outline-none"
              />
              <button
                type="button"
                onClick={() => removeStarterQuestion(q.id)}
                className="p-1.5 rounded-lg text-foreground-muted hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
                title={t('common.delete')}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        {/* Add Question Form */}
        <form onSubmit={handleAddQuestion} className="flex gap-2 pt-1">
          <input
            type="text"
            value={newQuestionText}
            onChange={(e) => setNewQuestionText(e.target.value)}
            placeholder={t('widget.starterPlaceholder')}
            className="flex-1 px-3 py-2 text-xs rounded-xl border border-surface-border bg-surface-elevated text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          <button
            type="submit"
            disabled={!newQuestionText.trim()}
            className="px-3.5 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-fg hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center gap-1.5 flex-shrink-0 shadow-sm"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>{t('widget.addStarterBtn')}</span>
          </button>
        </form>
      </div>

      <hr className="border-surface-border" />

      {/* 2. RAG Knowledge Base Connection */}
      <div className="space-y-4">
        <h3 className="text-sm font-heading font-bold text-foreground flex items-center gap-2">
          <Database className="w-4 h-4 text-primary" />
          <span>{t('widget.ragHeading')}</span>
        </h3>
        <p className="text-xs text-foreground-muted">
          {t('widget.ragDesc')}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => setAssociatedKnowledgeBase('all')}
            className={`p-3 rounded-xl border text-left transition-all ${
              config.associatedKnowledgeBase === 'all'
                ? 'border-primary bg-primary/10 ring-1 ring-primary'
                : 'border-surface-border bg-surface hover:bg-surface-elevated'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Layers className="w-4 h-4 text-primary" />
              <span className="text-xs font-bold text-foreground">{t('widget.allKnowledgeBase')}</span>
            </div>
            <p className="text-[11px] text-foreground-muted">
              {t('widget.allKnowledgeBaseDesc')}
            </p>
          </button>

          <button
            type="button"
            onClick={() => setAssociatedKnowledgeBase('specific')}
            className={`p-3 rounded-xl border text-left transition-all ${
              config.associatedKnowledgeBase === 'specific'
                ? 'border-primary bg-primary/10 ring-1 ring-primary'
                : 'border-surface-border bg-surface hover:bg-surface-elevated'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-accent" />
              <span className="text-xs font-bold text-foreground">{t('widget.specificDocs')}</span>
            </div>
            <p className="text-[11px] text-foreground-muted">
              {t('widget.specificDocsDesc')}
            </p>
          </button>

          <button
            type="button"
            onClick={() => setAssociatedKnowledgeBase('none')}
            className={`p-3 rounded-xl border text-left transition-all ${
              config.associatedKnowledgeBase === 'none'
                ? 'border-primary bg-primary/10 ring-1 ring-primary'
                : 'border-surface-border bg-surface hover:bg-surface-elevated'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="w-4 h-4 rounded-full border border-foreground-muted flex items-center justify-center text-[10px]">
                ✕
              </span>
              <span className="text-xs font-bold text-foreground">{t('widget.noRag')}</span>
            </div>
            <p className="text-[11px] text-foreground-muted">
              {t('widget.noRagDesc')}
            </p>
          </button>
        </div>
      </div>

      <hr className="border-surface-border" />

      {/* 3. Auto-Open Trigger */}
      <div className="space-y-3">
        <h3 className="text-sm font-heading font-bold text-foreground flex items-center gap-2">
          <Clock className="w-4 h-4 text-primary" />
          <span>{t('widget.autoOpenHeading')}</span>
        </h3>

        <div className="flex items-center gap-4">
          {[
            { label: t('widget.autoOpenDisabled'), value: 0 },
            { label: t('widget.autoOpenAfter', { seconds: 3 }), value: 3 },
            { label: t('widget.autoOpenAfter', { seconds: 5 }), value: 5 },
            { label: t('widget.autoOpenAfter', { seconds: 10 }), value: 10 },
          ].map((item) => {
            const isSelected = config.appearance.autoOpenDelaySeconds === item.value;
            return (
              <button
                key={item.value}
                type="button"
                onClick={() => updateAppearance({ autoOpenDelaySeconds: item.value })}
                className={`px-3 py-1.5 text-xs rounded-xl border font-medium transition-colors ${
                  isSelected
                    ? 'border-primary bg-primary text-primary-fg font-bold'
                    : 'border-surface-border bg-surface hover:bg-surface-elevated text-foreground-muted'
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
